import { type Ref, ref, watch } from "vue";
import type { PendingAudio, PendingFile, PendingImage } from "../api/attachments";

export interface QueuedMessage {
	id: string;
	text: string;
	images: PendingImage[];
	audio?: PendingAudio;
	files?: PendingFile[];
	accepted: boolean;
}

/** Keep every draft until the server consumes it, including during reconnection. */
export function useTurnQueue(deps: {
	streaming: Ref<boolean>;
	lost: Ref<boolean>;
	post: (message: QueuedMessage) => Promise<{ accepted: boolean }>;
	send: (
		text: string,
		images: PendingImage[],
		audio?: PendingAudio,
		files?: PendingFile[],
	) => Promise<void>;
}) {
	const queued = ref<QueuedMessage[]>([]);
	let flushing = false;
	let disposed = false;
	let retry: ReturnType<typeof setTimeout> | undefined;

	async function flush(): Promise<void> {
		if (disposed || flushing || !queued.value.length) return;
		flushing = true;
		try {
			if (deps.streaming.value) {
				for (const message of queued.value) {
					if (message.accepted) continue;
					const result = await deps.post(message);
					if (!result.accepted) break;
					message.accepted = true;
				}
			} else if (!deps.lost.value) {
				await sendBatch();
			}
		} catch {
			// A lost ACK is retried with the same IDs; the server deduplicates them.
		} finally {
			flushing = false;
			if (!disposed && queued.value.some((m) => !m.accepted)) {
				clearTimeout(retry);
				retry = setTimeout(() => void flush(), 2_000);
			}
		}
	}

	async function sendBatch(): Promise<void> {
		let textSize = 0;
		let imageCount = 0;
		let count = 0;
		for (const message of queued.value) {
			// Voz e arquivo viajam cada um no seu turno, como a primeira mensagem do lote.
			if (
				count &&
				(message.audio ||
					queued.value[0]?.audio ||
					message.files?.length ||
					queued.value[0]?.files?.length)
			)
				break;
			if (
				count &&
				(textSize + message.text.length + 2 > 20_000 || imageCount + message.images.length > 6)
			)
				break;
			textSize += message.text.length + 2;
			imageCount += message.images.length;
			count++;
		}
		const batch = queued.value.splice(0, count);
		const files = batch[0]?.files;
		if (batch.length)
			await deps.send(
				batch.map((m) => m.text).join("\n\n"),
				batch.flatMap((m) => m.images),
				batch[0]?.audio,
				// Só quando há: o lote sem arquivo segue exatamente como antes.
				...(files?.length ? [files] : []),
			);
	}

	function enqueue(payload: {
		text: string;
		images: PendingImage[];
		audio?: PendingAudio;
		files?: PendingFile[];
	}): void {
		queued.value.push({ ...payload, id: crypto.randomUUID(), accepted: false });
		void flush();
	}

	function acknowledge(id: string, applied: boolean): QueuedMessage | undefined {
		const message = queued.value.find((m) => m.id === id);
		if (applied) queued.value = queued.value.filter((m) => m.id !== id);
		else if (message) message.accepted = true;
		return message;
	}

	watch(deps.streaming, (running) => {
		if (!running) for (const message of queued.value) message.accepted = false;
		void flush();
	});

	return {
		queued,
		enqueue,
		acknowledge,
		flush,
		dispose: () => {
			disposed = true;
			clearTimeout(retry);
		},
	};
}
